// ============================================================================
// Onboardings de facturation électronique (BC Solutions), vus par un admin.
//
// Les statuts sont ceux que pose l'API Fast Facture dans users/{uid}.bcs
// (src/bcs/bcs-onboarding.service.ts). Les libellés, eux, diffèrent de l'app
// mobile À DESSEIN : là-bas ils s'adressent à l'utilisateur (« votre
// compte »), ici à l'opérateur qui suit le parcours.
// ============================================================================

export type BcsOnboardingStatus =
  | "creating_tenant"
  | "provisioning"
  | "revealing_credentials"
  | "creating_legalunit"
  | "starting_onboarding"
  | "awaiting_client"
  | "reconciling"
  | "kyb_pending"
  | "creating_office"
  | "active"
  | "error";

// Sous-ensemble de users/{uid}.bcs utile à l'affichage. Les identifiants du
// tenant (clientId, clientSecret, apiAccessId) ne sont PAS déclarés : aucun
// écran ne doit pouvoir les afficher par mégarde.
export type BcsOnboardingState = {
  onboardingStatus?: BcsOnboardingStatus;
  tenantSlug?: string;
  updatedAt?: number;
  target?: {
    name?: string;
    organizationName?: string;
    identifier?: string;
    typeSiret?: string;
    siren?: string;
    siret?: string | null;
  };
  onboarding?: {
    providerStatus?: string | null;
    url?: string | null;
    expiresAt?: string | null;
    restartAttempts?: number;
  } | null;
  error?: {
    code?: string | null;
    status?: number | null;
    message?: string;
    detail?: string | null;
  } | null;
  lastTransientError?: string;
};

// Étapes « en cours » : celles que le planificateur reprend. « active » est
// terminé, donc absent. « error » est ajouté à la liste affichée : un
// onboarding en erreur n'est pas fini, et c'est justement celui qui demande
// l'attention d'un admin — le planificateur, lui, ne le reprend pas.
const PENDING: BcsOnboardingStatus[] = [
  "creating_tenant",
  "provisioning",
  "revealing_credentials",
  "creating_legalunit",
  "starting_onboarding",
  "awaiting_client",
  "reconciling",
  "kyb_pending",
  "creating_office",
];

export function isOnboardingOpen(status?: string): boolean {
  return (
    !!status &&
    (PENDING.includes(status as BcsOnboardingStatus) || status === "error")
  );
}

type Step = { label: string; color: string; hint?: string };

// Trois familles, trois couleurs : automatique (rien à faire), en attente du
// client (relancer peut aider), en erreur (à traiter).
const AUTO = "#2a5bd7";
const CLIENT = "#c8761b";
const ERROR = "#c0392b";

const STEPS: Record<BcsOnboardingStatus, Step> = {
  creating_tenant: { label: "Création du tenant", color: AUTO },
  provisioning: { label: "Provisionnement", color: AUTO },
  revealing_credentials: { label: "Récupération des accès", color: AUTO },
  creating_legalunit: { label: "Enregistrement de l'entreprise", color: AUTO },
  starting_onboarding: { label: "Préparation du parcours", color: AUTO },
  awaiting_client: {
    label: "En attente du client",
    color: CLIENT,
    hint: "Le client doit réaliser sa vérification d'identité via le lien.",
  },
  reconciling: { label: "Validation en cours", color: AUTO },
  kyb_pending: { label: "Vérification KYB", color: AUTO },
  creating_office: { label: "Enregistrement de l'établissement", color: AUTO },
  active: { label: "Activé", color: "#1a8a4f" },
  error: { label: "En erreur", color: ERROR },
};

export function getOnboardingStep(status?: string): Step {
  return (
    STEPS[status as BcsOnboardingStatus] ?? {
      label: status || "Inconnu",
      color: AUTO,
    }
  );
}

// État du lien d'identification (Magic Link) : c'est l'information la plus
// utile quand un client est bloqué — le lien a-t-il expiré ?
export function getMagicLinkState(
  ob?: BcsOnboardingState["onboarding"]
): { label: string; expired: boolean } | undefined {
  if (!ob?.url) return undefined;
  if (!ob.expiresAt) return { label: "Lien disponible", expired: false };
  const at = Date.parse(ob.expiresAt);
  const expired = !Number.isNaN(at) && at <= Date.now();
  const when = new Date(at).toLocaleString("fr-FR", {
    dateStyle: "short",
    timeStyle: "short",
  });
  return expired
    ? { label: `Lien expiré le ${when}`, expired: true }
    : { label: `Lien valide jusqu'au ${when}`, expired: false };
}
